import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import {
  ArrowLeft,
  Save,
  User,
  Users,
  GraduationCap,
  Briefcase,
  Award,
  Shield,
  Key,
} from 'lucide-react';
import { containerVariants, itemVariants } from '../../utils/animations';
import { useToast } from '../../context/ToastContext';

import PersonalTab from '../../components/hris/employee/form/PersonalTab';
import FamilyTab from '../../components/hris/employee/form/FamilyTab';
import EducationTab from '../../components/hris/employee/form/EducationTab';
import EmploymentTab from '../../components/hris/employee/form/EmploymentTab';
import CertificationTab from '../../components/hris/employee/form/CertificationTab';
import AccountTab from '../../components/hris/employee/form/AccountTab';
import PunishmentTab from '../../components/hris/employee/form/PunishmentTab';

const INITIAL_FORM_DATA = {
  employee_id: '',
  nip: '',
  full_name: '',
  photo_url: '',
  nik: '',
  birth_place: '',
  birth_date: '',
  address: '',
  marital_status: '',
  phone: '',
  email: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  ktp_url: '',
  gender: '',
  family_members: [],
  education_history: [],
  department_id: '',
  position_id: '',
  manager_id: '',
  employment_status: 'permanent',
  join_date: '',
  contract_start_date: '',
  end_date: '',
  work_location: '',
  certifications: [],
  base_salary: '',
  grade_id: '',
  competency_level: 3,
  bank_account: '',
  bank_name: '',
  npwp: '',
  bpjs_kesehatan: '',
  bpjs_ketenagakerjaan: '',
  credit_approval_limit: '',
  status: 'active',
  password: '',
  confirm_password: '',
};

const TABS = [
  { id: 'personal', label: 'Data Pribadi', icon: User },
  { id: 'family', label: 'Keluarga', icon: Users },
  { id: 'education', label: 'Pendidikan', icon: GraduationCap },
  { id: 'employment', label: 'Kepegawaian', icon: Briefcase },
  { id: 'certification', label: 'Sertifikasi', icon: Award },
  { id: 'account', label: 'Akun Login', icon: Key },
  { id: 'punishment', label: 'Punishment', icon: Shield },
];

export default function EmployeeForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  // Temporarily disable permission check to test login issue
  // const { canAccess } = usePermissionCheck();
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeId = searchParams.get('id');
  const isEdit = !!employeeId;

  // Temporarily disable permission checks to test login
  /*
  // Check permissions
  const canView = canAccess('hris_emp.employee.view');
  const canCreate = canAccess('hris_emp.employee.create');
  const canUpdate = canAccess('hris_emp.employee.update');

  // Redirect if user doesn't have permission to view
  React.useEffect(() => {
    if (!canView) {
      showError('Anda tidak memiliki akses untuk melihat data pegawai');
      navigate('/');
      return;
    }

    if (isEdit && !canUpdate) {
      showError('Anda tidak memiliki akses untuk mengedit data pegawai');
      navigate('/');
      return;
    }

    if (!isEdit && !canCreate) {
      showError('Anda tidak memiliki akses untuk menambah pegawai baru');
      navigate('/');
      return;
    }
  }, [canView, canCreate, canUpdate, isEdit, navigate, showError]);
  */

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [salaryPolicies, setSalaryPolicies] = useState([]);
  const [calculatedSalary, setCalculatedSalary] = useState<any>(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const skipLoadRef = useRef(false); // Flag to skip loadData after create

  const calculateSalary = React.useCallback(
    (positionId, joinDate, competencyLevel) => {
      if (!salaryPolicies || salaryPolicies.length === 0) {
        setCalculatedSalary(null);
        return null;
      }

      const activePolicy = salaryPolicies.find(p => p.is_active === true || p.is_active === 'true');
      if (!activePolicy) {
        setCalculatedSalary(null);
        return null;
      }

      if (!positionId) {
        setCalculatedSalary(null);
        return null;
      }

      if (!positions || positions.length === 0) {
        setCalculatedSalary(null);
        return null;
      }

      // Try to find position by id (handle both string and number)
      const position = positions.find(
        p =>
          p.id === positionId || p.id === String(positionId) || String(p.id) === String(positionId)
      );

      if (!position) {
        setCalculatedSalary(null);
        return null;
      }

      if (!position.level && position.level !== 0) {
        setCalculatedSalary(null);
        return null;
      }

      // Get total_grades from policy
      const totalGrades = activePolicy.total_grades || 10;

      // Two Point Formula: Mid Salary = a + (b × level)
      // where b = (top_salary - base_salary) / (total_grades - 1)
      // and a = base_salary - b
      const baseSalary = parseFloat(activePolicy.base_salary) || 0;
      const topSalary = parseFloat(activePolicy.top_salary) || 0;
      const b = (topSalary - baseSalary) / (totalGrades - 1);
      const a = baseSalary - b;
      const midSalary = a + b * Number(position.level);
      let calculatedValue = midSalary;
      let tenureAdjustment = 0;
      let competencyAdjustment = 0;

      // Calculate tenure adjustment - follows generateSalaryScales logic in OrganizationStructure
      if ((activePolicy.use_tenure_adjustment || activePolicy.toggle_tenure) && joinDate) {
        const joinDateObj = new Date(joinDate);
        const now = new Date();
        const years = Math.floor(
          (now.getTime() - joinDateObj.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
        );
        const maxYears = activePolicy.max_years || 10;
        const effectiveYears = Math.min(Math.max(years, 0), maxYears);

        if (effectiveYears > 0) {
          // Follow logic: salary * (tenure_increment_percent / 100) * tenure
          if (activePolicy.tenure_increment_percent) {
            const incrementPercent = parseFloat(activePolicy.tenure_increment_percent) || 0;
            tenureAdjustment = midSalary * (incrementPercent / 100) * effectiveYears;
          } else if (activePolicy.yearly_increment) {
            // Fallback: if using yearly_increment (nominal per year)
            tenureAdjustment = parseFloat(activePolicy.yearly_increment) * effectiveYears;
          }

          calculatedValue += tenureAdjustment;
        }
      }

      // Calculate competency adjustment - follows generateSalaryScales logic in OrganizationStructure
      if (
        (activePolicy.use_competency_adjustment || activePolicy.toggle_competency) &&
        competencyLevel
      ) {
        let multiplier = 1.0;

        // Follow logic: salary = salary * multiplier
        if (activePolicy.competency_multiplier) {
          if (typeof activePolicy.competency_multiplier === 'string') {
            try {
              const parsed = JSON.parse(activePolicy.competency_multiplier);
              multiplier = parsed[competencyLevel.toString()] || 1.0;
            } catch (e) {
              // Error parsing competency_multiplier
            }
          } else {
            multiplier = activePolicy.competency_multiplier[competencyLevel.toString()] || 1.0;
          }

          // Apply multiplier to current salary (midSalary + tenureAdjustment)
          const salaryBeforeCompetency = midSalary + tenureAdjustment;
          calculatedValue = salaryBeforeCompetency * multiplier;
          competencyAdjustment = calculatedValue - salaryBeforeCompetency;
        } else if (activePolicy.competency_factor) {
          // Fallback: if using competency_factor (nominal per level)
          // Level 3 is base (no adjustment), level 1-2 reduced, level 4-5 increased
          const levelDiff = competencyLevel - 3; // -2, -1, 0, 1, 2
          competencyAdjustment = parseFloat(activePolicy.competency_factor) * levelDiff;
          calculatedValue = midSalary + tenureAdjustment + competencyAdjustment;
        }
      }

      const result = {
        midSalary: Math.round(midSalary),
        tenureAdjustment: Math.round(tenureAdjustment),
        competencyAdjustment: Math.round(competencyAdjustment),
        total: Math.round(calculatedValue),
        policyName: activePolicy.policy_name || activePolicy.name,
      };

      setCalculatedSalary(result);

      // Don't update formData here to avoid re-render loop
      // Return the calculated value so caller can update if needed
      return Math.round(calculatedValue);
    },
    [salaryPolicies, positions]
  );

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [empData, deptData, posData, salaryData] = await Promise.all([
        apiClient.get('/hris/employees'),
        apiClient.get('/hris/departments'),
        apiClient.get('/hris/positions'),
        apiClient.get('/hris/organization').catch(err => {
          return { salaryPolicies: [] };
        }),
      ]);

      const depts = deptData.departments || deptData || [];
      const poss = posData.positions || posData || [];
      const emps = empData.employees || empData || [];
      const policies = salaryData.salaryPolicies || [];

      setDepartments(depts);
      setPositions(poss);
      setEmployees(emps);
      setSalaryPolicies(policies);

      if (isEdit) {
        const empDetail = await apiClient.get(`/hris/employees/${employeeId}`);
        if (empDetail) {
          const nipValue = empDetail.employee_id || empDetail.nip || '';
          const updatedFormData = {
            ...INITIAL_FORM_DATA,
            ...empDetail,
            ktp_url: empDetail.ktp_file_url || empDetail.ktp_url || '',
            base_salary: empDetail.base_salary?.toString() || '',
            competency_level: empDetail.competency_level || 3,
            family_members: empDetail.family_members || [],
            education_history: empDetail.education_history || [],
            certifications: empDetail.certifications || [],
            nip: nipValue,
            employee_id: nipValue,
          };
          setFormData(updatedFormData);

          // Recalculate salary if position and join date exist
          // This will be handled by useEffect after state updates
        }
      }
    } catch (error: any) {
      showError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, [isEdit, employeeId, showError]);

  useEffect(() => {
    // Skip loadData if we just created an employee
    if (skipLoadRef.current) {
      skipLoadRef.current = false;
      return;
    }
    loadData();
  }, [loadData]);

  // Don't auto-recalculate salary on every change to avoid re-render loop
  // Only calculate when user explicitly changes position, join_date, or competency_level

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePositionChange = positionId => {
    const selectedPosition = positions.find(p => p.id === positionId);
    if (selectedPosition) {
      // Calculate salary first
      const salary = calculateSalary(
        positionId,
        formData.join_date,
        formData.competency_level || 3
      );

      // Update all fields in one call
      setFormData(prev => ({
        ...prev,
        position_id: positionId,
        department_id: selectedPosition.department_id || prev.department_id,
        base_salary: salary ? salary.toString() : prev.base_salary,
      }));
    } else {
      handleChange('position_id', positionId);
    }
  };

  const handleJoinDateChange = date => {
    // Calculate salary first
    const salary = calculateSalary(formData.position_id, date, formData.competency_level || 3);

    // Update all fields in one call
    setFormData(prev => ({
      ...prev,
      join_date: date,
      base_salary: salary ? salary.toString() : prev.base_salary,
    }));
  };

  const handleCompetencyChange = level => {
    // Calculate salary first
    const salary = calculateSalary(formData.position_id, formData.join_date, level);

    // Update all fields in one call
    setFormData(prev => ({
      ...prev,
      competency_level: level,
      base_salary: salary ? salary.toString() : prev.base_salary,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Get fields for each tab
  const getTabFields = (tabId: string) => {
    switch (tabId) {
      case 'personal':
        return {
          full_name: formData.full_name,
          photo_url: formData.photo_url,
          nik: formData.nik,
          birth_place: formData.birth_place,
          birth_date:
            formData.birth_date && formData.birth_date.trim() !== '' ? formData.birth_date : null,
          address: formData.address,
          marital_status:
            formData.marital_status && formData.marital_status.trim() !== ''
              ? formData.marital_status
              : null,
          phone: formData.phone,
          email: formData.email,
          emergency_contact_name: formData.emergency_contact_name,
          emergency_contact_phone: formData.emergency_contact_phone,
          ktp_file_url: formData.ktp_url,
          gender: formData.gender && formData.gender.trim() !== '' ? formData.gender : null,
        };
      case 'family':
        return {
          family_members: formData.family_members || [],
        };
      case 'education':
        return {
          education_history: formData.education_history || [],
        };
      case 'employment': {
        // Helper to normalize integer fields
        const normalizeInt = (value: any): number | null => {
          if (!value || value === '' || value === 'null' || value === 'undefined') {
            return null;
          }
          const parsed = parseInt(value, 10);
          return isNaN(parsed) ? null : parsed;
        };

        // Helper to normalize decimal/numeric fields
        const normalizeDecimal = (value: any): number | null => {
          if (!value || value === '' || value === 'null' || value === 'undefined') {
            return null;
          }
          if (typeof value === 'number') {
            return isNaN(value) ? null : value;
          }
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
              return null;
            }
            const parsed = parseFloat(trimmed);
            return isNaN(parsed) ? null : parsed;
          }
          return null;
        };

        return {
          employee_id: formData.employee_id,
          department_id: normalizeInt(formData.department_id),
          position_id: normalizeInt(formData.position_id),
          manager_id: normalizeInt(formData.manager_id),
          employment_status: formData.employment_status,
          join_date:
            formData.join_date && formData.join_date.trim() !== '' ? formData.join_date : null,
          contract_start_date:
            formData.contract_start_date && formData.contract_start_date.trim() !== ''
              ? formData.contract_start_date
              : null,
          end_date: formData.end_date && formData.end_date.trim() !== '' ? formData.end_date : null,
          work_location: formData.work_location,
          base_salary: normalizeDecimal(formData.base_salary),
          grade_id: normalizeInt(formData.grade_id),
          competency_level: normalizeInt(formData.competency_level) || 3,
          bank_account: formData.bank_account,
          bank_name: formData.bank_name,
          npwp: formData.npwp,
          bpjs_kesehatan: formData.bpjs_kesehatan,
          bpjs_ketenagakerjaan: formData.bpjs_ketenagakerjaan,
          credit_approval_limit: normalizeDecimal(formData.credit_approval_limit),
          status: formData.status,
        };
      }
      case 'certification':
        return {
          certifications: formData.certifications || [],
        };
      case 'account':
        return {
          nip: formData.nip || null,
          password: formData.password || null,
        };
      case 'punishment':
        return {}; // Punishment tab doesn't have fields yet
      default:
        return {};
    }
  };

  const validateTab = (tabId: string) => {
    const newErrors: Record<string, string> = {};

    if (tabId === 'personal') {
      if (!formData.full_name || formData.full_name.trim() === '') {
        newErrors.full_name = 'Nama lengkap harus diisi';
      }
      if (!formData.email || formData.email.trim() === '') {
        newErrors.email = 'Email harus diisi';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Format email tidak valid';
      }
      if (formData.nik && formData.nik.trim() !== '') {
        if (!/^\d{16}$/.test(formData.nik)) {
          newErrors.nik = 'NIK harus 16 digit angka';
        }
      }
    }

    if (tabId === 'employment') {
      if (!formData.employee_id || formData.employee_id.trim() === '') {
        newErrors.employee_id = 'ID Pegawai harus diisi';
      }
      if (!formData.department_id) {
        newErrors.department_id = 'Divisi harus dipilih';
      }
    }

    if (tabId === 'account' && !isEdit) {
      if (!formData.nip || formData.nip.trim() === '') {
        newErrors.nip = 'NIP harus diisi';
      }
      if (!formData.password || formData.password.trim() === '') {
        newErrors.password = 'Password harus diisi';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password minimal 6 karakter';
      }
      if (!formData.confirm_password || formData.confirm_password.trim() === '') {
        newErrors.confirm_password = 'Konfirmasi password harus diisi';
      } else if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Password tidak cocok';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleButtonClick = async () => {
    // Validate current tab
    if (!validateTab(activeTab)) {
      showError('Mohon lengkapi data yang wajib diisi');
      return;
    }

    // For new employee, validate only fields relevant to current tab
    if (!isEdit) {
      // For personal tab, only check personal fields
      if (activeTab === 'personal') {
        if (!formData.full_name || formData.full_name.trim() === '') {
          showError('Nama lengkap harus diisi');
          return;
        }
        if (!formData.email || formData.email.trim() === '') {
          showError('Email harus diisi');
          return;
        }
      }
      // For employment tab, check employee_id
      if (activeTab === 'employment') {
        if (!formData.employee_id || formData.employee_id.trim() === '') {
          showError('ID Pegawai harus diisi');
          return;
        }
        if (!formData.department_id) {
          showError('Divisi harus dipilih untuk hak akses sistem');
          return;
        }
      }
    }

    setSaving(true);

    try {
      // Get only fields for current tab
      const tabFields = getTabFields(activeTab);

      if (isEdit) {
        // Partial update - only update fields from current tab
        await apiClient.put(`/hris/employees/${employeeId}`, tabFields);
        showSuccess(
          `Data ${TABS.find(t => t.id === activeTab)?.label || 'pegawai'} berhasil disimpan`
        );
      } else {
        // Helper to normalize integer fields
        const normalizeInt = (value: any): number | null => {
          if (!value || value === '' || value === 'null' || value === 'undefined') {
            return null;
          }
          const parsed = parseInt(value, 10);
          return isNaN(parsed) ? null : parsed;
        };

        // Helper to normalize decimal/numeric fields
        const normalizeDecimal = (value: any): number | null => {
          if (!value || value === '' || value === 'null' || value === 'undefined') {
            return null;
          }
          if (typeof value === 'number') {
            return isNaN(value) ? null : value;
          }
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
              return null;
            }
            const parsed = parseFloat(trimmed);
            return isNaN(parsed) ? null : parsed;
          }
          return null;
        };

        // For new employee, merge with existing formData but prioritize tab fields
        // First normalize all formData values
        const normalizedFormData = {
          ...formData,
          base_salary: normalizeDecimal(formData.base_salary),
          department_id: normalizeInt(formData.department_id),
          position_id: normalizeInt(formData.position_id),
          manager_id: normalizeInt(formData.manager_id),
          grade_id: normalizeInt(formData.grade_id),
          competency_level: normalizeInt(formData.competency_level) || 3,
          credit_approval_limit: normalizeDecimal(formData.credit_approval_limit),
        };

        // Then merge with tab fields (tab fields take priority)
        const dataToSave: any = {
          ...normalizedFormData,
          ...tabFields,
        };

        // Generate temporary employee_id if not provided (required by database)
        if (!dataToSave.employee_id || dataToSave.employee_id.trim() === '') {
          // Generate temporary ID: TEMP-{timestamp}
          const timestamp = Date.now();
          dataToSave.employee_id = `TEMP-${timestamp}`;
        }

        // Map ktp_url to ktp_file_url for backend
        if (dataToSave.ktp_url) {
          dataToSave.ktp_file_url = dataToSave.ktp_url;
          delete dataToSave.ktp_url;
        }

        // Normalize gender - convert empty string to null
        if (dataToSave.gender && dataToSave.gender.trim() === '') {
          dataToSave.gender = null;
        }

        // Normalize marital_status - convert empty string to null
        if (dataToSave.marital_status && dataToSave.marital_status.trim() === '') {
          dataToSave.marital_status = null;
        }

        const response = await apiClient.post('/hris/employees', dataToSave);
        showSuccess('Pegawai baru berhasil ditambahkan');

        // Update URL with new employee ID to enable editing and stay on the same page
        const newEmployee = response?.employee || response;
        if (newEmployee?.id) {
          const newEmployeeId = newEmployee.id;

          // Set flag to skip loadData on next render
          skipLoadRef.current = true;

          setSearchParams({ id: newEmployeeId.toString() });
          // Update form data with the created employee data
          const updatedFormData = {
            ...INITIAL_FORM_DATA,
            ...newEmployee,
            ktp_url: newEmployee.ktp_file_url || newEmployee.ktp_url || '',
            base_salary: newEmployee.base_salary?.toString() || '',
            competency_level: newEmployee.competency_level || 3,
            family_members: newEmployee.family_members || [],
            education_history: newEmployee.education_history || [],
            certifications: newEmployee.certifications || [],
          };
          setFormData(updatedFormData);

          // Recalculate salary if position and join date exist
          if (updatedFormData.position_id && salaryPolicies.length > 0 && positions.length > 0) {
            calculateSalary(
              updatedFormData.position_id,
              updatedFormData.join_date,
              updatedFormData.competency_level || 3
            );
          }
        }
      }
    } catch (error: any) {
      showError(error?.message || 'Gagal menyimpan data pegawai');
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return <PersonalTab formData={formData} handleChange={handleChange} errors={errors} />;
      case 'family':
        return <FamilyTab formData={formData} setFormData={setFormData} />;
      case 'education':
        return <EducationTab formData={formData} setFormData={setFormData} />;
      case 'employment':
        return (
          <EmploymentTab
            formData={formData}
            handleChange={handleChange}
            handleJoinDateChange={handleJoinDateChange}
            handlePositionChange={handlePositionChange}
            handleCompetencyChange={handleCompetencyChange}
            departments={departments}
            positions={positions}
            employees={employees}
            employeeId={employeeId}
            errors={errors}
            calculatedSalary={calculatedSalary}
          />
        );
      case 'certification':
        return <CertificationTab formData={formData} setFormData={setFormData} />;
      case 'account':
        return (
          <AccountTab
            formData={formData}
            handleChange={handleChange}
            errors={errors}
            isEdit={isEdit}
          />
        );
      case 'punishment':
        return <PunishmentTab />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {saving && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-4 bg-white rounded-lg p-8 shadow-xl">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
            <p className="text-emerald-600 font-medium text-lg">Menyimpan data pegawai...</p>
            <p className="text-gray-500 text-sm">Mohon tunggu sebentar</p>
          </div>
        </div>
      )}

      <motion.div
        className="space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Pegawai' : 'Tambah Pegawai Baru'}
            </h1>
            <p className="text-gray-500 mt-1">
              {isEdit ? 'Perbarui data pegawai' : 'Isi data lengkap pegawai baru'}
            </p>
          </div>
        </motion.div>

        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          {/* Tabs */}
          <motion.div
            variants={itemVariants}
            className="bg-gray-100 p-1 rounded-lg flex flex-wrap gap-1 mb-6"
          >
            {TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </motion.div>

          {/* Tab Content */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border p-6">
            {renderTabContent()}
          </motion.div>

          {/* Submit Button - Hidden for punishment tab (view only) */}
          {activeTab !== 'punishment' && (
            <motion.div variants={itemVariants} className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100 hover:border-gray-400 transition-all duration-300"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleButtonClick}
                disabled={saving}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:scale-95 flex items-center gap-2 disabled:opacity-50 transition-all duration-300"
              >
                <Save className="w-4 h-4" />
                {saving
                  ? 'Menyimpan...'
                  : `Simpan ${TABS.find(t => t.id === activeTab)?.label || 'Data'}`}
              </button>
            </motion.div>
          )}
        </form>
      </motion.div>
    </div>
  );
}
